// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  seasonApplicationsQueryOptions,
  seasonApplicationRuleCatalogQueryOptions,
  seasonContextQueryOptions,
  seasonDetailQueryOptions,
  seasonListQueryOptions,
} from '@/modules/season/season.hooks'

export const seasonRoute = createRoute({
  path: '/seasons',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const seasonListRoute = createRoute({
  getParentRoute: () => seasonRoute,
  path: '/',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'seasons.list',
      'Sezon listesine erişim izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Sezonlar' }] }),
  loader: async () => {
    await queryClient.ensureQueryData(seasonListQueryOptions())
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/season/pages/list-season.page')
  ),
})

export const seasonCreateRoute = createRoute({
  getParentRoute: () => seasonRoute,
  path: '/create',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'seasons.create',
      'Sezon oluşturma izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Yeni Sezon' }] }),
  loader: async () => {
    await Promise.all([
      queryClient.ensureQueryData(seasonContextQueryOptions()),
      queryClient.ensureQueryData(seasonApplicationRuleCatalogQueryOptions()),
    ])
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/season/pages/season-builder.page')
  ),
})

export const seasonDetailRoute = createRoute({
  getParentRoute: () => seasonRoute,
  path: '/$id',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'seasons.detail',
      'Sezon detayına erişim izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Sezon Detayı' }] }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(seasonContextQueryOptions()),
      queryClient.ensureQueryData(seasonDetailQueryOptions(id)),
    ])
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/season/pages/season-detail.page')
  ),
})

export const seasonApplicationsRoute = createRoute({
  getParentRoute: () => seasonRoute,
  path: '/$id/applications',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'seasons.detail',
      'Sezon başvurularına erişim izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Sezon Başvuruları' }] }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(seasonApplicationsQueryOptions(id)),
      queryClient.ensureQueryData(seasonApplicationRuleCatalogQueryOptions()),
    ])
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/season/pages/season-applications.page')
  ),
})

export const seasonEditRoute = createRoute({
  getParentRoute: () => seasonRoute,
  path: '/$id/edit',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(preload, 'seasons.edit', 'Sezon düzenleme izniniz yok.'),
  head: () => ({ meta: [{ title: 'Sezon Düzenle' }] }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(seasonContextQueryOptions()),
      queryClient.ensureQueryData(seasonApplicationRuleCatalogQueryOptions()),
      queryClient.ensureQueryData(seasonDetailQueryOptions(id)),
    ])
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/season/pages/season-builder.page')
  ),
})
